import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, TypographyProps, Button, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

// ═══════════════════════════════════════════════════════════════════════
// ██  TYPEWRITER HOOK & COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function useTypewriter(
  text: string,
  _speed: number = 35,
  _delay: number = 0,
) {
  // Render full text immediately — no character-by-character animation.
  // Typewriter effects cause visual replay on re-mount and look gimmicky
  // on a clinical tool. Kept hook signature for backwards compatibility.
  return { displayed: text, isComplete: true };
}

interface TypewriterTextProps {
  text: string;
  speed?: number;
  delay?: number;
  showCursor?: boolean;
  sx?: TypographyProps['sx'];
  variant?: TypographyProps['variant'];
  component?: React.ElementType;
  onComplete?: () => void;
}

export function TypewriterText({
  text,
  speed = 35,
  delay = 0,
  showCursor = true,
  sx,
  variant = 'h4',
  component,
  onComplete,
}: TypewriterTextProps) {
  const { displayed, isComplete } = useTypewriter(text, speed, delay);

  useEffect(() => {
    if (isComplete && onComplete) onComplete();
  }, [isComplete, onComplete]);

  return (
    <Typography
      variant={variant}
      component={component || ('span' as React.ElementType)}
      sx={{
        ...((sx as any) || {}),
        display: 'inline',
      }}
    >
      {displayed}
    </Typography>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  PAGE TRANSITION WRAPPER
// ═══════════════════════════════════════════════════════════════════════

const pageVariants = {
  initial: {
    opacity: 0,
    y: 12,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 1, 1] as [number, number, number, number],
    },
  },
};

export function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  STAGGERED CHILDREN WRAPPER
// ═══════════════════════════════════════════════════════════════════════

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const childVariants = {
  initial: { opacity: 0, y: 16 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
  },
};

export function StaggerContainer({
  children,
  stagger = 0.08,
  delayChildren = 0.15,
}: {
  children: React.ReactNode;
  stagger?: number;
  delayChildren?: number;
}) {
  return (
    <motion.div
      variants={{
        initial: {},
        animate: {
          transition: { staggerChildren: stagger, delayChildren },
        },
      }}
      initial="initial"
      animate="animate"
    >
      {children}
    </motion.div>
  );
}

export function StaggerChild({ children }: { children: React.ReactNode }) {
  return <motion.div variants={childVariants}>{children}</motion.div>;
}

// ═══════════════════════════════════════════════════════════════════════
// ██  FADE-IN ON SCROLL (IntersectionObserver)
// ═══════════════════════════════════════════════════════════════════════

export function FadeInOnScroll({
  children,
  threshold = 0.15,
}: {
  children: React.ReactNode;
  threshold?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  ANIMATED COUNTER (for numerical displays)
// ═══════════════════════════════════════════════════════════════════════

export function AnimatedCounter({
  value,
  duration = 800,
  suffix = '',
  prefix = '',
  sx,
}: {
  value: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  sx?: TypographyProps['sx'];
}) {
  const [display, setDisplay] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <Typography component="span" sx={sx}>
      {prefix}
      {display}
      {suffix}
    </Typography>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  GLOW HOVER SX HELPERS (for MUI sx prop)
// ═══════════════════════════════════════════════════════════════════════

export const glowHoverSx = {
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow:
      '0 8px 25px rgba(45, 125, 70, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  '&:active': {
    transform: 'translateY(-1px) scale(0.98)',
    boxShadow: '0 4px 15px rgba(45, 125, 70, 0.2)',
  },
};

export const glowHoverSubtleSx = {
  transition: 'all 0.25s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(200, 160, 80, 0.12)',
  },
};

export const buttonGlowSx = {
  transition: 'all 0.25s ease',
  '&:hover': {
    filter: 'brightness(1.08)',
    boxShadow: '0 6px 20px rgba(45, 125, 70, 0.25)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    filter: 'brightness(0.95)',
    transform: 'translateY(0px) scale(0.97)',
  },
};

// Warm focus ring for input fields
export const warmFocusRingSx = {
  '&.Mui-focused': {
    boxShadow: '0 0 0 3px rgba(45, 125, 70, 0.15)',
  },
};

// ═══════════════════════════════════════════════════════════════════════
// ██  ANIMATED MIC BUTTON (idle → recording → processing)
// ═══════════════════════════════════════════════════════════════════════

export type MicState = 'idle' | 'recording' | 'processing';

interface AnimatedMicButtonProps {
  state: MicState;
  onClick: () => void;
  disabled?: boolean;
  size?: number;
  timerSeconds?: number;
  label?: string;
}

export function AnimatedMicButton({
  state,
  onClick,
  disabled = false,
  size = 48,
  timerSeconds = 0,
  label,
}: AnimatedMicButtonProps) {
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          position: 'relative',
          width: size + 16,
          height: size + 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Pulsing rings for recording state — pointerEvents:none so clicks reach the button */}
        {state === 'recording' && (
          <>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(239, 68, 68, 0.3)',
                pointerEvents: 'none',
                animation: 'micPulseRing 1.5s ease-out infinite',
                '@keyframes micPulseRing': {
                  '0%': { transform: 'scale(0.8)', opacity: 1 },
                  '100%': { transform: 'scale(1.6)', opacity: 0 },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(239, 68, 68, 0.2)',
                pointerEvents: 'none',
                animation: 'micPulseRing 1.5s ease-out infinite 0.5s',
              }}
            />
          </>
        )}
        {/* Spinning arc for processing state */}
        {state === 'processing' && (
          <Box
            sx={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#10B981',
              pointerEvents: 'none',
              animation: 'micSpin 1s linear infinite',
              '@keyframes micSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        )}
        <motion.button
          onClick={onClick}
          disabled={disabled || state === 'processing'}
          whileHover={state === 'idle' ? { scale: 1.08, y: -2 } : {}}
          whileTap={state === 'idle' ? { scale: 0.95 } : {}}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: 'none',
            cursor: disabled || state === 'processing' ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              state === 'recording'
                ? '#EF4444'
                : state === 'processing'
                  ? '#F1F5F9'
                  : '#F0FDF4',
            color:
              state === 'recording'
                ? '#FFFFFF'
                : state === 'processing'
                  ? '#10B981'
                  : '#10B981',
            transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow:
              state === 'recording'
                ? '0 4px 14px rgba(239, 68, 68, 0.35)'
                : '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <AnimatePresence mode="wait">
            {state === 'recording' ? (
              <motion.div
                key="stop"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex' }}
              >
                <StopIcon sx={{ fontSize: size * 0.5 }} />
              </motion.div>
            ) : state === 'processing' ? (
              <motion.div
                key="spin"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex' }}
              >
                <CircularProgress size={size * 0.45} sx={{ color: '#10B981' }} />
              </motion.div>
            ) : (
              <motion.div
                key="mic"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex' }}
              >
                <MicIcon sx={{ fontSize: size * 0.5 }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </Box>
      {state === 'recording' && timerSeconds > 0 && (
        <Typography
          variant="caption"
          sx={{ color: '#EF4444', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatTime(timerSeconds)}
        </Typography>
      )}
      {label && (
        <Typography variant="caption" sx={{ color: '#64748B', fontSize: 11 }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  FIELD FILL ANIMATION (flash highlight when value populates)
// ═══════════════════════════════════════════════════════════════════════

interface FieldFillAnimationProps {
  value: string;
  delay?: number;
  children: React.ReactNode;
}

export function FieldFillAnimation({ value, delay = 0, children }: FieldFillAnimationProps) {
  const [flash, setFlash] = useState(false);
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current === '' && value !== '') {
      const timer = setTimeout(() => {
        setFlash(true);
        setTimeout(() => setFlash(false), 600);
      }, delay);
      return () => clearTimeout(timer);
    }
    prevValue.current = value;
  }, [value, delay]);

  return (
    <motion.div
      animate={
        flash
          ? {
              scale: [1, 1.02, 1],
              boxShadow: [
                '0 0 0 0 rgba(16, 185, 129, 0)',
                '0 0 0 4px rgba(16, 185, 129, 0.2)',
                '0 0 0 0 rgba(16, 185, 129, 0)',
              ],
            }
          : {}
      }
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ borderRadius: 12 }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  SAVE BUTTON — full state machine
// ═══════════════════════════════════════════════════════════════════════

export type SaveState = 'idle' | 'saving' | 'success' | 'error';

interface SaveButtonProps {
  onClick: () => void;
  disabled?: boolean;
  state: SaveState;
  label?: string;
}

export function SaveButton({ onClick, disabled = false, state, label = 'Save Patient' }: SaveButtonProps) {
  return (
    <motion.div
      animate={
        state === 'error'
          ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
          : state === 'success'
            ? { scale: [1, 1.04, 1] }
            : {}
      }
      transition={
        state === 'error'
          ? { duration: 0.5, ease: 'easeInOut' }
          : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
      }
      style={{ display: 'inline-block' }}
    >
      <Button
        onClick={onClick}
        disabled={disabled || state === 'saving'}
        sx={{
          background:
            state === 'success'
              ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
              : state === 'error'
                ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '24px',
          padding: '30px 60px',
          minHeight: '100px',
          borderRadius: '16px',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow:
            state === 'success'
              ? '0 8px 24px rgba(16, 185, 129, 0.35)'
              : state === 'error'
                ? '0 8px 24px rgba(239, 68, 68, 0.35)'
                : '0 4px 14px rgba(16, 185, 129, 0.2)',
          '&:hover': {
            transform: disabled ? 'none' : 'translateY(-3px)',
            boxShadow: disabled
              ? undefined
              : '0 12px 28px rgba(16, 185, 129, 0.3)',
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          },
          '&:active': {
            transform: 'scale(0.97)',
          },
        }}
      >
        <AnimatePresence mode="wait">
          {state === 'saving' ? (
            <motion.div
              key="saving"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <CircularProgress size={24} sx={{ color: 'white' }} />
              Saving...
            </motion.div>
          ) : state === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <CheckIcon /> Saved!
            </motion.div>
          ) : state === 'error' ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              Error — Try Again
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <CheckIcon /> {label}
            </motion.div>
          )}
        </AnimatePresence>
      </Button>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ██  SUCCESS CELEBRATION — lightweight burst of sparkles
// ═══════════════════════════════════════════════════════════════════════

const SPARKLE_COLORS = ['#10B981', '#34D399', '#6EE7B7', '#3B82F6', '#A78BFA'];

/**
 * Renders an animated burst of sparkle particles.
 * Auto-dismisses after the animation completes (~1.5s).
 */
export function SuccessCelebration({
  show,
  message = 'Done!',
}: {
  show: boolean;
  message?: string;
}) {
  if (!show) return null;

  const particles = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * 360;
    const distance = 40 + Math.random() * 40;
    const rad = (angle * Math.PI) / 180;
    return {
      x: Math.cos(rad) * distance,
      y: Math.sin(rad) * distance,
      color: SPARKLE_COLORS[i % SPARKLE_COLORS.length],
      size: 4 + Math.random() * 4,
      delay: Math.random() * 0.2,
    };
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          position: 'relative',
        }}
      >
        {/* Sparkle particles */}
        {particles.map((p, i) => (
          <motion.div
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x: p.x, y: p.y, opacity: 0, scale: 0 }}
            transition={{ duration: 1.2, delay: p.delay, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              background: p.color,
            }}
          />
        ))}

        {/* Center checkmark */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 15, delay: 0.1 }}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(16,185,129,0.3)',
          }}
        >
          <CheckIcon style={{ color: 'white', fontSize: 28 }} />
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: '#059669', textAlign: 'center' }}
          >
            {message}
          </Typography>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
